import { useCallback, useEffect, useState } from "react";
import { BarChart3, Wallet, TrendingUp, Users, Percent } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid } from "recharts";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { growService } from "@/services/growService";
import { ANALYTICS_RANGES, goalTypeLabel, isMoneyGoal } from "@/config/growOptions";
import { fmtCents } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = ["#0B2545", "#C9A24B", "#3B6B8F", "#7C9CBF", "#9C7B3B", "#5A7D6E", "#B0563F"];
const Metric = ({ icon: Icon, label, value, testid }) => (
  <Card data-testid={testid}><CardContent className="p-4">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Icon className="h-3.5 w-3.5 text-accent" /> {label}</div>
    <p className="mt-1.5 font-heading text-2xl font-bold text-foreground">{value}</p>
  </CardContent></Card>
);

export default function Analytics() {
  const [preset, setPreset] = useState("ytd");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = preset === "custom" ? { preset, start: custom.start, end: custom.end } : { preset };
      if (preset === "custom" && (!custom.start || !custom.end)) { setLoading(false); return; }
      const [ov, ts, gs] = await Promise.all([growService.overview(params), growService.timeseries(params), growService.listGoals("active")]);
      setData(ov); setSeries(ts.series); setGoals(gs);
    } catch { toast.error("Could not load analytics"); }
    finally { setLoading(false); }
  }, [preset, custom]);
  useEffect(() => { load(); }, [load]);

  const comm = data?.commission_lifetime || { estimated: 0, pending: 0, paid: 0 };
  const funnel = (data?.pipeline || []).filter((s) => !s.is_lost_stage);
  const sources = (data?.lead_sources || []).slice(0, 7).map((s) => ({ name: s.source, value: s.leads }));

  return (
    <AppShell>
      <div className="space-y-6" data-testid="analytics-page">
        <PageHeader eyebrow="AgentHQ · Grow" title="Analytics"
          description="Your real business, measured. Every number is computed from your AgentHQ data — no estimates or benchmarks."
          actions={
            <div className="flex items-center gap-2">
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger className="w-[150px]" data-testid="analytics-range"><SelectValue /></SelectTrigger>
                <SelectContent>{ANALYTICS_RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          } />

        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} className="w-[160px]" data-testid="analytics-start" />
            <span className="text-muted-foreground">to</span>
            <Input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} className="w-[160px]" data-testid="analytics-end" />
          </div>
        )}

        {/* Commission Summary Bar (lifetime, reconciles to records) */}
        <div className="grid gap-3 sm:grid-cols-3" data-testid="commission-summary-bar">
          <Card className="border-l-4 border-l-brand-navy"><CardContent className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Estimated</p><p className="mt-1 font-heading text-2xl font-bold text-brand-navy" data-testid="commission-estimated">{fmtCents(comm.estimated)}</p></CardContent></Card>
          <Card className="border-l-4 border-l-amber-500"><CardContent className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending</p><p className="mt-1 font-heading text-2xl font-bold text-amber-600" data-testid="commission-pending">{fmtCents(comm.pending)}</p></CardContent></Card>
          <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Paid</p><p className="mt-1 font-heading text-2xl font-bold text-emerald-600" data-testid="commission-paid">{fmtCents(comm.paid)}</p></CardContent></Card>
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading analytics" /></div> : !data ? (
          <EmptyState title="Your business story will appear here as you work in AgentHQ." description="Close transactions, add leads and log commissions to see analytics." />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric icon={TrendingUp} label="Closed deals" value={data.transactions.closed_count} testid="metric-closed" />
              <Metric icon={Wallet} label="Closed volume" value={fmtCents(data.transactions.closed_volume)} testid="metric-volume" />
              <Metric icon={Users} label="Leads created" value={data.leads.created} testid="metric-leads" />
              <Metric icon={Percent} label="Conversion" value={`${data.leads.conversion_rate}%`} testid="metric-conversion" />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="conversion-definition">Conversion: {data.conversion_definition}</p>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card><CardContent className="p-5">
                <h3 className="mb-3 text-sm font-bold text-foreground">Volume & paid commission over time</h3>
                {series.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No closed business in this range yet.</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={series.map((s) => ({ ...s, volume: s.volume / 100, paid_commission: s.paid_commission / 100 }))}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} width={40} />
                      <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                      <Bar dataKey="volume" name="Volume" fill="#0B2545" radius={[4,4,0,0]} />
                      <Bar dataKey="paid_commission" name="Paid commission" fill="#C9A24B" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent></Card>

              <Card><CardContent className="p-5">
                <h3 className="mb-3 text-sm font-bold text-foreground">Lead sources</h3>
                {sources.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No lead source data yet.</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                        {sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent></Card>

              <Card><CardContent className="p-5">
                <h3 className="mb-1 text-sm font-bold text-foreground">Pipeline funnel</h3>
                <p className="mb-3 text-xs text-muted-foreground">Current leads by stage · avg days in stage</p>
                <div className="space-y-2" data-testid="pipeline-funnel">
                  {funnel.map((s) => (
                    <div key={s.code} className="flex items-center gap-2">
                      <div className="w-40 shrink-0 truncate text-xs text-muted-foreground">{s.stage}</div>
                      <div className="h-6 flex-1 rounded bg-secondary">
                        <div className="flex h-6 items-center rounded bg-brand-navy px-2 text-[10px] font-medium text-white" style={{ width: `${Math.max(8, Math.min(100, s.count * 12))}%` }}>{s.count}</div>
                      </div>
                      <div className="w-16 shrink-0 text-right text-[11px] text-muted-foreground">{s.avg_days_in_stage != null ? `${s.avg_days_in_stage}d` : "—"}</div>
                    </div>
                  ))}
                </div>
              </CardContent></Card>

              <Card><CardContent className="p-5">
                <h3 className="mb-3 text-sm font-bold text-foreground">Goal progress</h3>
                {goals.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No active goals. Set one in Goals.</p> : (
                  <div className="space-y-3" data-testid="analytics-goals">
                    {goals.map((g) => (
                      <div key={g.id}>
                        <div className="flex justify-between text-xs"><span className="font-medium text-foreground">{goalTypeLabel(g.goal_type)}</span><span className="text-muted-foreground">{g.progress_percent}%</span></div>
                        <Progress value={g.progress_percent} className="mt-1 h-2" />
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{isMoneyGoal(g.goal_type) ? fmtCents(g.current_value) : g.current_value} of {isMoneyGoal(g.goal_type) ? fmtCents(g.target_value) : g.target_value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
